export type AnimationName =
  | "none"
  | "fade"
  | "fade-up"
  | "slide"
  | "scale"
  | "blur"
  | "clip"
  | "stagger"
  | "spring";
export type SectionType =
  | "hero"
  | "content"
  | "services"
  | "pixel-swap"
  | "gallery"
  | "stats"
  | "scroll-expand"
  | "testimonials"
  | "hours"
  | "blog"
  | "faq"
  | "cta";

export interface AnimationConfig {
  name: AnimationName;
  duration: number;
  delay: number;
  easing: string;
  intensity: number;
}
export interface StyleConfig {
  background: string;
  color?: string;
  padding: number;
  radius: number;
  gap: number;
  columns: number;
  align: "left" | "center" | "right";
  shadow: number;
}

export interface TextStyle {
  fontSize?: number;
  fontWeight?: number;
  color?: string;
  align?: "left" | "center" | "right";
  lineHeight?: number;
  letterSpacing?: number;
  maxWidth?: number;
  offsetX?: number;
  offsetY?: number;
}

export type TextStyleMap = Record<string, TextStyle>;

export interface Asset {
  id: string;
  r2Key?: string;
  url: string;
  name: string;
  alt: string;
  contentType: string;
  size: number;
  uploadedAt?: string;
  source: "bundled" | "upload";
}

export interface Item {
  id: string;
  title: string;
  text?: string;
  label?: string;
  image?: string;
  assetId?: string;
  alt?: string;
  icon?: string;
  color?: string;
  category?: string;
  date?: string;
  link?: string;
  value?: string;
  focalX?: number;
  focalY?: number;
  radius?: number;
  overlay?: number;
  zoom?: number;
  aspectRatio?: string;
  brightness?: number;
  background?: string;
  cardRadius?: number;
  cardShadow?: number;
  spacing?: number;
  cardAlign?: "left" | "center" | "right";
  textStyles?: TextStyleMap;
}
export interface Section {
  id: string;
  type: SectionType;
  content: Record<string, string | number | boolean | Item[]>;
  style: StyleConfig;
  layout: string;
  animation: AnimationConfig;
  textStyles?: TextStyleMap;
}
export interface NavItem {
  id: string;
  label: string;
  href: string;
  textStyle?: TextStyle;
}

export interface BrandConfig {
  name: string;
  icon: "sandbox" | "sun" | "sparkles";
  ariaLabel: string;
}

export interface GlobalContent {
  footerDescription: string;
  contactLabel: string;
  email: string;
  location: string;
  socialLabel: string;
  instagramLabel: string;
  instagramHref: string;
  linkedinLabel: string;
  linkedinHref: string;
  copyright: string;
  deliveryNote: string;
  backToTop: string;
}
export interface Theme {
  background: string;
  surface: string;
  text: string;
  muted: string;
  primary: string;
  secondary: string;
  accent: string;
  buttonText: string;
  border: string;
  radius: number;
}
export interface SiteConfig {
  version: number;
  theme: Theme;
  brand: BrandConfig;
  globalContent: GlobalContent;
  globalTextStyles?: TextStyleMap;
  assets: Asset[];
  navigation: NavItem[];
  sections: Section[];
}
export interface Selection {
  sectionId?: string;
  kind: "section" | "text" | "image" | "item" | "icon" | "link" | "global" | "nav";
  field?: string;
  itemId?: string;
  navId?: string;
  elementId?: string;
  linkField?: string;
}

export const defaultStyle = (background = "transparent"): StyleConfig => ({
  background,
  padding: 112,
  radius: 0,
  gap: 24,
  columns: 3,
  align: "left",
  shadow: 0,
});
export const defaultAnimation = (
  name: AnimationName = "fade-up",
): AnimationConfig => ({
  name,
  duration: 0.75,
  delay: 0,
  easing: "power3.out",
  intensity: 24,
});
